import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, X, AlertCircle, LogOut, Edit, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const AddStudent = () => {
  const [hostelStudents, setHostelStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [payments, setPayments] = useState([]);
  const [fines, setFines] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [checkoutData, setCheckoutData] = useState(null);
  const [checkoutDate, setCheckoutDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    student_id: '',
    student_name: '',
    hostel_id: '',
    hostel_name: '',
    room_id: '',
    room_number: '',
    admission_date: new Date().toISOString().split('T')[0],
    monthly_fee: 0,
    remarks: ''
  });

  useEffect(() => {
    fetchHostelStudents();
    fetchAllStudents();
    fetchHostels();
    fetchPayments();
    fetchFines();
  }, []);

  const fetchHostelStudents = async () => {
    try {
      const response = await apiClient.get('/api/hostel/students');
      setHostelStudents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchAllStudents = async () => {
    try {
      const response = await apiClient.get('/api/students/dropdown');
      setAllStudents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchHostels = async () => {
    try {
      const response = await apiClient.get('/api/hostel/hostels');
      setHostels(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchRooms = async (hostelId) => {
    try {
      const response = await apiClient.get(`/api/hostel/rooms?hostel_id=${hostelId}`);
      setRooms(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await apiClient.get('/api/hostel/fee-payments');
      setPayments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchFines = async () => {
    try {
      const response = await apiClient.get('/api/hostel/fines');
      setFines(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleStudentChange = (studentId) => {
    const student = allStudents.find(s => s.id === studentId);
    if (student) {
      setFormData({
        ...formData,
        student_id: student.admission_number || studentId,
        student_name: student.full_name
      });
    }
  };

  const handleHostelChange = (hostelId) => {
    const hostel = hostels.find(h => h.id === hostelId);
    if (hostel) {
      // Auto-fill monthly fee from hostel settings
      setFormData({ 
        ...formData, 
        hostel_id: hostelId, 
        hostel_name: hostel.hostel_name, 
        room_id: '', 
        room_number: '',
        monthly_fee: hostel.monthly_fee || 0  // Auto-fill from hostel fee settings
      });
      fetchRooms(hostelId);
    }
  };

  const handleRoomChange = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    if (room) setFormData({ ...formData, room_id: roomId, room_number: room.room_number });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEditMode && editingId) {
        // Edit existing hostel student — only allowed fields
        await apiClient.put(`/api/hostel/students/${editingId}`, {
          hostel_id: formData.hostel_id,
          hostel_name: formData.hostel_name,
          room_id: formData.room_id,
          room_number: formData.room_number,
          admission_date: formData.admission_date,
          monthly_fee: formData.monthly_fee,
          remarks: formData.remarks,
        });
        alert('Hostel student updated!');
      } else {
        const response = await apiClient.post('/api/hostel/students', formData);
        if (response.status === 200 || response.data) {
          alert('Student added to hostel!');
        }
      }
      fetchHostelStudents();
      handleCloseModal();
    } catch (error) {
      alert(isEditMode ? 'Failed to update student' : 'Failed to add student');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (student) => {
    setIsEditMode(true);
    setEditingId(student.id);
    setFormData({
      student_id: student.student_id || '',
      student_name: student.student_name || '',
      hostel_id: student.hostel_id || '',
      hostel_name: student.hostel_name || '',
      room_id: student.room_id || '',
      room_number: student.room_number || '',
      admission_date: student.admission_date || new Date().toISOString().split('T')[0],
      monthly_fee: student.monthly_fee || 0,
      remarks: student.remarks || '',
    });
    if (student.hostel_id) {
      fetchRooms(student.hostel_id);
    }
    setIsModalOpen(true);
  };

  const handleDelete = async (student) => {
    if (!window.confirm(`Delete ${student.student_name} from hostel? This will also free up the room.`)) return;
    try {
      await apiClient.delete(`/api/hostel/students/${student.id}`);
      alert('Hostel student deleted.');
      fetchHostelStudents();
    } catch (error) {
      alert('Failed to delete student. ' + (error.response?.data?.detail || ''));
    }
  };

  // Calculate dues for a student up to a specific date
  const calculateDues = (student, upToDate = new Date()) => {
    const admissionDate = new Date(student.admission_date);
    const checkoutDate = new Date(upToDate);
    const checkoutYear = checkoutDate.getFullYear();
    const checkoutMonth = checkoutDate.getMonth();
    
    // Get paid months
    const studentPayments = payments.filter(p => 
      p.student_id === student.id || p.payer_name === student.student_name
    );
    const paidMonths = studentPayments.map(p => `${p.month}-${p.year}`);
    
    // Calculate due months only up to checkout date
    const dueMonthsList = [];
    let startYear = admissionDate.getFullYear();
    let startMonth = admissionDate.getMonth();
    
    for (let year = startYear; year <= checkoutYear; year++) {
      const monthStart = (year === startYear) ? startMonth : 0;
      const monthEnd = (year === checkoutYear) ? checkoutMonth : 11;
      
      for (let month = monthStart; month <= monthEnd; month++) {
        const monthName = MONTHS[month];
        const key = `${monthName}-${year}`;
        
        if (!paidMonths.includes(key)) {
          dueMonthsList.push({ month: monthName, year, key });
        }
      }
    }
    
    // Get pending fines
    const studentFines = fines.filter(f => 
      f.student_id === student.id || f.student_name === student.student_name
    );
    const pendingFines = studentFines.filter(f => f.payment_status !== 'Paid');
    const pendingFineAmount = pendingFines.reduce((sum, f) => sum + (f.fine_amount - (f.paid_amount || 0)), 0);
    
    return {
      dueMonths: dueMonthsList,
      dueMonthsCount: dueMonthsList.length,
      feeDue: dueMonthsList.length * student.monthly_fee,
      pendingFines: pendingFineAmount,
      totalDue: (dueMonthsList.length * student.monthly_fee) + pendingFineAmount,
      canCheckout: dueMonthsList.length === 0 && pendingFineAmount === 0
    };
  };

  const handleCheckoutClick = (student) => {
    setSelectedStudent(student);
    const today = new Date().toISOString().split('T')[0];
    setCheckoutDate(today);
    const dues = calculateDues(student, new Date(today));
    setCheckoutData(dues);
    setIsCheckoutModalOpen(true);
  };

  const handleCheckoutDateChange = (newDate) => {
    setCheckoutDate(newDate);
    if (selectedStudent) {
      const dues = calculateDues(selectedStudent, new Date(newDate));
      setCheckoutData(dues);
    }
  };

  const handleCheckout = async () => {
    if (!selectedStudent) return;
    
    if (!checkoutData.canCheckout) {
      alert('Cannot checkout! Please clear all dues first.');
      return;
    }
    
    setLoading(true);
    try {
      await apiClient.put(`/api/hostel/students/${selectedStudent.id}/checkout?checkout_date=${checkoutDate}`);
      alert('Student checked out successfully!');
      fetchHostelStudents();
      setIsCheckoutModalOpen(false);
      setSelectedStudent(null);
      setCheckoutData(null);
      setCheckoutDate(new Date().toISOString().split('T')[0]);
    } catch (error) {
      alert('Failed to checkout student');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingId(null);
    setFormData({
      student_id: '',
      student_name: '',
      hostel_id: '',
      hostel_name: '',
      room_id: '',
      room_number: '',
      admission_date: new Date().toISOString().split('T')[0],
      monthly_fee: 0,
      remarks: ''
    });
    setRooms([]);
  };

  // Filter students not already in hostel
  const availableStudents = allStudents.filter(
    s => !hostelStudents.some(hs => hs.student_id === s.admission_number || hs.student_name === s.full_name)
  );

  return (
    <div className="p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Hostel Students</CardTitle>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />Add Student
          </Button>
        </CardHeader>
        <CardContent>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-2">Student Name</th>
                <th className="text-left p-2">Student ID</th>
                <th className="text-left p-2">Hostel</th>
                <th className="text-left p-2">Room</th>
                <th className="text-left p-2">Admission</th>
                <th className="text-right p-2">Fee/Month</th>
                <th className="text-center p-2">Status</th>
                <th className="text-center p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {hostelStudents.length === 0 ? (
                <tr><td colSpan="8" className="text-center p-4 text-gray-500">No students in hostel.</td></tr>
              ) : (
                hostelStudents.map((student) => {
                  const dues = calculateDues(student);
                  return (
                    <tr key={student.id} className="border-b hover:bg-gray-50">
                      <td className="p-2 font-medium">{student.student_name}</td>
                      <td className="p-2">{student.student_id}</td>
                      <td className="p-2">{student.hostel_name}</td>
                      <td className="p-2">{student.room_number}</td>
                      <td className="p-2">{formatDate(student.admission_date)}</td>
                      <td className="p-2 text-right">₹{student.monthly_fee}</td>
                      <td className="p-2 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${student.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {student.status}
                          </span>
                          {dues.totalDue > 0 && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800 whitespace-nowrap">
                              Due: ₹{dues.totalDue}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-2 text-center">
                        <div className="flex gap-1 justify-center">
                          {student.status === 'Active' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditClick(student)}
                                data-testid={`hostel-edit-btn-${student.id}`}
                                title="Edit"
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCheckoutClick(student)}
                                title="Checkout"
                              >
                                <LogOut className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(student)}
                            data-testid={`hostel-delete-btn-${student.id}`}
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Add / Edit Student Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isEditMode ? 'Edit Hostel Student' : 'Add Student to Hostel'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isEditMode && (
              <div>
                <Label>Select Student *</Label>
                <Select onValueChange={handleStudentChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="-- Select Student from Database --" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableStudents.length === 0 ? (
                      <SelectItem value="none" disabled>No students available</SelectItem>
                    ) : (
                      availableStudents.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.full_name} ({s.admission_number || 'N/A'})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.student_name && (
              <div className="bg-blue-50 p-3 rounded-lg text-sm">
                <p><strong>Selected:</strong> {formData.student_name}</p>
                <p><strong>Admission No:</strong> {formData.student_id}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hostel *</Label>
                <Select value={formData.hostel_id} onValueChange={handleHostelChange}>
                  <SelectTrigger><SelectValue placeholder="Select hostel" /></SelectTrigger>
                  <SelectContent>
                    {hostels.map((h) => (
                      <SelectItem key={h.id} value={h.id}>{h.hostel_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Room *</Label>
                <Select value={formData.room_id} onValueChange={handleRoomChange} disabled={!formData.hostel_id}>
                  <SelectTrigger>
                    <SelectValue placeholder={formData.hostel_id ? "Select room" : "Select hostel first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {rooms.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.room_number} ({r.current_occupancy}/{r.capacity})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Check-in Date *</Label>
                <Input type="date" required value={formData.admission_date} onChange={(e) => setFormData({ ...formData, admission_date: e.target.value })} />
                <p className="text-xs text-gray-500 mt-1">Fee calculation starts from this date</p>
              </div>
              <div>
                <Label>Monthly Fee (₹) *</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  required 
                  value={formData.monthly_fee} 
                  onChange={(e) => setFormData({ ...formData, monthly_fee: parseFloat(e.target.value) || 0 })} 
                  className={formData.hostel_id && formData.monthly_fee > 0 ? 'bg-green-50' : ''}
                />
                {formData.hostel_id && formData.monthly_fee > 0 && (
                  <p className="text-xs text-green-600 mt-1">Auto-filled from hostel settings</p>
                )}
              </div>
            </div>

            <div>
              <Label>Remarks</Label>
              <Input value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} placeholder="Optional remarks" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
              <Button type="submit" disabled={loading || !formData.student_name || !formData.hostel_id || !formData.room_id}>
                {loading ? 'Saving...' : (isEditMode ? 'Update Student' : 'Add Student')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Checkout Modal */}
      <Dialog open={isCheckoutModalOpen} onOpenChange={setIsCheckoutModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Student Checkout</DialogTitle>
          </DialogHeader>
          
          {selectedStudent && checkoutData && (
            <div className="space-y-3">
              {/* Student Info - Compact */}
              <div className="bg-gray-50 p-2 rounded text-sm grid grid-cols-2 gap-1">
                <span><strong>Student:</strong> {selectedStudent.student_name}</span>
                <span><strong>Room:</strong> {selectedStudent.room_number}</span>
                <span><strong>Admission:</strong> {formatDate(selectedStudent.admission_date)}</span>
                <span><strong>Fee:</strong> ₹{selectedStudent.monthly_fee}/month</span>
              </div>

              {/* Checkout Date */}
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">Checkout Date:</Label>
                <Input 
                  type="date" 
                  value={checkoutDate} 
                  onChange={(e) => handleCheckoutDateChange(e.target.value)}
                  min={selectedStudent.admission_date}
                  className="flex-1"
                />
              </div>

              {/* Dues Summary - Compact */}
              <div className="text-sm space-y-1">
                <div className="flex justify-between p-1.5 bg-gray-100 rounded">
                  <span>Fee Due ({checkoutData.dueMonthsCount} months)</span>
                  <span className={checkoutData.feeDue > 0 ? 'text-red-600 font-bold' : 'text-green-600'}>₹{checkoutData.feeDue}</span>
                </div>
                <div className="flex justify-between p-1.5 bg-gray-100 rounded">
                  <span>Pending Fines</span>
                  <span className={checkoutData.pendingFines > 0 ? 'text-red-600 font-bold' : 'text-green-600'}>₹{checkoutData.pendingFines}</span>
                </div>
                <div className="flex justify-between p-2 bg-purple-100 rounded font-bold">
                  <span>Total Due</span>
                  <span className={checkoutData.totalDue > 0 ? 'text-red-600' : 'text-green-600'}>₹{checkoutData.totalDue}</span>
                </div>
              </div>

              {/* Due Months - Compact scrollable */}
              {checkoutData.dueMonths.length > 0 && (
                <div className="bg-red-50 p-2 rounded border border-red-200 max-h-20 overflow-y-auto">
                  <p className="text-red-700 text-xs font-medium mb-1">Due: {checkoutData.dueMonths.map(d => `${d.month.slice(0,3)} ${d.year}`).join(', ')}</p>
                </div>
              )}

              {/* Status Message */}
              {checkoutData.canCheckout ? (
                <p className="text-green-700 text-sm bg-green-50 p-2 rounded">✅ All dues cleared!</p>
              ) : (
                <p className="text-red-700 text-sm bg-red-50 p-2 rounded">❌ Clear ₹{checkoutData.totalDue} dues first</p>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCheckoutModalOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCheckout} 
                  disabled={loading || !checkoutData.canCheckout}
                  className={checkoutData.canCheckout ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  {loading ? 'Processing...' : 'Confirm Checkout'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddStudent;
